/**
 * Gate kiosk (G2) — the desk's home: entry tiles, today's numbers, live
 * activity feed (pending rows glow), shortcuts to messages / kid exits.
 * Polls every 12s so approvals appear without touching anything.
 */
import { router, useFocusEffect } from 'expo-router';
import React from 'react';
import { Pressable, View } from 'react-native';

import { space, type PillStatus } from '@bero/tokens';
import { num, str, extractItems } from '@/components/gate/api';
import { BrandLockup } from '@/components/brand';
import { Icon, type IconName } from '@/components/gate/icons';
import { Icon as NavIcon } from '@/features/account-admin/icons';
import { useT } from '@/i18n';
import type { DictKey } from '@/i18n/en';
import { api } from '@/lib/client';
import { useSession } from '@/lib/session';
import { useTheme } from '@/theme';
import { AppText, Card, Pill, Screen, SectionLabel, Skeleton } from '@/ui';

type FeedRow = {
  id: string; name: string; type: string; flat: string; status: string; when: string;
  checkedIn: boolean; checkedOut: boolean;
};

function toFeed(v: unknown, i: number): FeedRow {
  const iso = str(v, 'arrivedAt', 'createdAt');
  const d = iso ? new Date(iso) : null;
  let when = '';
  if (d && !Number.isNaN(d.getTime())) {
    let h = d.getHours(); const am = h < 12 ? 'AM' : 'PM'; h = h % 12 || 12;
    when = `${h}:${String(d.getMinutes()).padStart(2, '0')} ${am}`;
  }
  return {
    id: str(v, 'id') ?? `f${i}`,
    name: str(v, 'name') ?? 'Visitor',
    type: (str(v, 'type') ?? 'VISITOR').toLowerCase(),
    flat: str(v, 'flatCode') ?? '',
    status: (str(v, 'status') ?? 'PENDING').toUpperCase(),
    when,
    checkedIn: !!str(v, 'checkedInAt'),
    checkedOut: !!str(v, 'checkedOutAt'),
  };
}

function feedPill(r: FeedRow): { status: PillStatus; label: DictKey } {
  if (r.status === 'PENDING') return { status: 'pending', label: 'pill_awaiting' };
  if (r.status === 'REJECTED') return { status: 'rejected', label: 'pill_rejected' };
  if (r.status === 'LEFT_AT_GATE') return { status: 'open', label: 'pill_at_gate' };
  if (r.checkedOut) return { status: 'open', label: 'pill_left' };
  if (r.checkedIn) return { status: 'approved', label: 'pill_inside' };
  return { status: 'approved', label: 'pill_approved' };
}

// entry type (API value, lowercased) → localised label, resolved at render.
const FEED_TYPE: Record<string, DictKey> = {
  visitor: 'vd_type_guest', guest: 'vd_type_guest',
  delivery: 'vd_type_delivery', cab: 'vd_type_cab', staff: 'vd_type_staff',
};

const TILES: { icon: IconName; label: DictKey; sub: DictKey; path: string }[] = [
  { icon: 'user', label: 'kiosk_visitor', sub: 'kiosk_visitor_sub', path: '/guard/entry?type=VISITOR' },
  { icon: 'package', label: 'kiosk_delivery', sub: 'kiosk_delivery_sub', path: '/guard/entry?type=DELIVERY' },
  { icon: 'car', label: 'kiosk_cab', sub: 'kiosk_cab_sub', path: '/guard/entry?type=CAB' },
  { icon: 'check-circle', label: 'validate_pass', sub: 'validate_pass_sub', path: '/guard/validate' },
  { icon: 'users', label: 'staff_punch', sub: 'staff_punch_sub', path: '/guard/punch' },
  { icon: 'list', label: 'todays_log', sub: 'todays_log_sub', path: '/guard/log' },
  { icon: 'alert-triangle', label: 'incident', sub: 'incident_sub', path: '/guard/incidents' },
];

export default function GuardKiosk() {
  const { t } = useT();
  const { colors } = useTheme();
  const { user, signOut } = useSession();
  const [signingOut, setSigningOut] = React.useState(false);
  const [feed, setFeed] = React.useState<FeedRow[] | null>(null);
  const [summary, setSummary] = React.useState<{ inside: number; pending: number; total: number; messages: number; kids: number } | null>(null);
  const [busyId, setBusyId] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    try {
      const [f, s] = await Promise.all([
        api.get<unknown>('/api/gate/feed'),
        api.get<unknown>('/api/gate/summary'),
      ]);
      setFeed(extractItems<unknown>(f, 'items').map(toFeed));
      const today = (s && typeof s === 'object' && 'today' in s) ? (s as { today: unknown }).today : {};
      setSummary({
        inside: num(today, 'inside') ?? 0,
        pending: num(today, 'pending') ?? 0,
        total: num(today, 'total') ?? 0,
        messages: num(s, 'unreadMessages') ?? 0,
        kids: num(s, 'activeKidExits') ?? 0,
      });
    } catch { /* keep last data */ }
  }, []);

  useFocusEffect(React.useCallback(() => {
    void Promise.resolve().then(() => load());
    const t = setInterval(() => void load(), 12000);
    return () => clearInterval(t);
  }, [load]));

  const act = async (r: FeedRow, action: 'checkin' | 'checkout') => {
    setBusyId(r.id);
    try {
      await api.post<unknown>(`/api/gate/entries/${encodeURIComponent(r.id)}/${action}`, {});
      await load();
    } catch { /* transient */ } finally {
      setBusyId(null);
    }
  };

  return (
    <Screen scroll={false} safeTop>
      <View style={{
        flexDirection: 'row', alignItems: 'center', paddingHorizontal: space.s4, paddingVertical: space.s3,
        borderBottomWidth: 0.5, borderBottomColor: colors.borderSoft, gap: space.s3,
      }}>
        <BrandLockup markHeight={22} />
        <View style={{ flex: 1, minWidth: 0 }}>
          <AppText variant="headingSm" weight="600">{t('main_gate')}</AppText>
          <AppText variant="bodySm" style={{ color: colors.success500 }}>
            {user?.fullName ?? 'Gate'} · {t('on_duty')}
          </AppText>
        </View>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t('lang_title')}
          onPress={() => router.push('/account/language')}
          hitSlop={8}
          style={{ padding: 4 }}
        >
          <NavIcon name="globe" size={20} color={colors.textSecondary} />
        </Pressable>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t('sign_out')}
          disabled={signingOut}
          onPress={() => { setSigningOut(true); void signOut(); }}
          hitSlop={8}
          style={{ opacity: signingOut ? 0.5 : 1 }}
        >
          <AppText variant="bodySm" weight="600" tone="tertiary">{t('sign_out')}</AppText>
        </Pressable>
      </View>

      <Screen padded style={{ paddingTop: space.s4 }}>
        {/* stats strip */}
        <View style={{ flexDirection: 'row', gap: space.s2 }}>
          {[
            [t('stat_inside'), summary?.inside], [t('stat_awaiting'), summary?.pending], [t('stat_today'), summary?.total],
          ].map(([label, value]) => (
            <View key={String(label)} style={{ flex: 1, backgroundColor: colors.canvas1, borderRadius: 12, padding: space.s3, alignItems: 'center' }}>
              <AppText mono weight="600" style={{ fontSize: 22, lineHeight: 28 }}>{value ?? '—'}</AppText>
              <AppText variant="label" tone="tertiary">{String(label)}</AppText>
            </View>
          ))}
        </View>

        {/* entry tiles — grouped: creating an entry vs working the desk
            (app-wide grouping convention, user ask 2026-07-18) */}
        {[
          { title: t('new_entry'), tiles: TILES.slice(0, 3) },
          { title: t('gate_desk_hdr'), tiles: TILES.slice(3) },
        ].map((grp) => (
          <View key={grp.title}>
            <SectionLabel style={{ marginTop: space.s5 }}>{grp.title}</SectionLabel>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: space.s2 }}>
              {grp.tiles.map((tile) => (
                <Pressable
                  key={tile.label}
                  accessibilityRole="button"
                  onPress={() => router.push(tile.path as '/guard')}
                  style={({ pressed }) => ({
                    width: '48.5%', backgroundColor: pressed ? colors.canvas2 : colors.canvas1,
                    borderRadius: 14, padding: space.s4, gap: 6,
                  })}
                >
                  <Icon name={tile.icon} size={22} color={colors.textPrimary} />
                  <AppText variant="bodyMd" weight="600">{t(tile.label)}</AppText>
                  <AppText variant="bodySm" tone="tertiary">{t(tile.sub)}</AppText>
                </Pressable>
              ))}
            </View>
          </View>
        ))}

        {/* messages + kid exits shortcuts */}
        <View style={{ flexDirection: 'row', gap: space.s2, marginTop: space.s3 }}>
          <Pressable
            accessibilityRole="button"
            onPress={() => router.push('/guard/messages' as '/guard')}
            style={{ flex: 1, flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: colors.canvas1, borderRadius: 12, padding: space.s3 }}
          >
            <Icon name="mail" size={18} color={colors.textPrimary} />
            <AppText variant="bodySm" weight="600" style={{ flex: 1 }}>{t('messages')}</AppText>
            {summary?.messages ? <Pill status="urgent" label={String(summary.messages)} /> : null}
          </Pressable>
          <Pressable
            accessibilityRole="button"
            onPress={() => router.push('/guard/kids' as '/guard')}
            style={{ flex: 1, flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: colors.canvas1, borderRadius: 12, padding: space.s3 }}
          >
            <Icon name="clock" size={18} color={colors.textPrimary} />
            <AppText variant="bodySm" weight="600" style={{ flex: 1 }}>{t('kid_exits')}</AppText>
            {summary?.kids ? <Pill status="approved" label={String(summary.kids)} /> : null}
          </Pressable>
        </View>

        {/* live feed */}
        <SectionLabel style={{ marginTop: space.s5 }}>{t('recent_activity')}</SectionLabel>
        {feed === null ? (
          <View style={{ gap: space.s3 }}>{[0, 1, 2].map((i) => <Skeleton key={i} h={76} />)}</View>
        ) : feed.length === 0 ? (
          <AppText variant="bodySm" tone="tertiary">{t('no_entries_today')}</AppText>
        ) : (
          <View style={{ gap: space.s3 }}>
            {feed.map((r) => {
              const p = feedPill(r);
              const awaiting = r.status === 'PENDING';
              return (
                <Card
                  key={r.id}
                  style={awaiting ? { borderWidth: 1.5, borderColor: colors.warning500 } : undefined}
                  onPress={awaiting ? () => router.push(`/guard/waiting?id=${r.id}` as '/guard') : undefined}
                >
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3 }}>
                    <View style={{ flex: 1, minWidth: 0 }}>
                      <AppText variant="bodyMd" weight="600">{r.name}</AppText>
                      <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>
                        {(FEED_TYPE[r.type] ? t(FEED_TYPE[r.type]) : r.type)} · {r.flat}{r.when ? ` · ${r.when}` : ''}
                      </AppText>
                    </View>
                    <Pill status={p.status} label={t(p.label)} />
                  </View>
                  {r.status === 'APPROVED' && !r.checkedOut ? (
                    <View style={{ flexDirection: 'row', gap: space.s2, marginTop: space.s3 }}>
                      {!r.checkedIn ? (
                        <Pressable
                          accessibilityRole="button"
                          onPress={() => void act(r, 'checkin')}
                          style={{ flex: 1, backgroundColor: colors.primary500, borderRadius: 10, minHeight: 46, justifyContent: 'center', alignItems: 'center', opacity: busyId === r.id ? 0.6 : 1 }}
                        >
                          <AppText variant="bodyMd" weight="600" style={{ color: colors.textOnDark }}>{t('mark_entered')}</AppText>
                        </Pressable>
                      ) : (
                        <Pressable
                          accessibilityRole="button"
                          onPress={() => void act(r, 'checkout')}
                          style={{ flex: 1, borderWidth: 1, borderColor: colors.borderFirm, borderRadius: 10, minHeight: 46, justifyContent: 'center', alignItems: 'center', opacity: busyId === r.id ? 0.6 : 1 }}
                        >
                          <AppText variant="bodyMd" weight="600">{t('mark_left')}</AppText>
                        </Pressable>
                      )}
                    </View>
                  ) : null}
                </Card>
              );
            })}
          </View>
        )}
      </Screen>
    </Screen>
  );
}
